/**
 * Shared pieces of the report views (batch 8 T14c, #3036): how a measured figure, an unknown one, a money amount and a
 * reconciliation are shown. Presentation only: nothing here sums, rounds or derives a figure.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { Alert, Box, Chip, Stack, Table, TableContainer, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { WireMetric, WirePagination, WireReconciliation, WireValueSource, WireWindow } from '@/hooks/useInventoryReports';
import { formatExactEuro, formatFigure, formatInstant, formatRoundedEuro, moneyView } from './reportFormat';
import { reasonKey } from './reportLabels';

export interface ReportDisplay {
  language: string;
  /** Show the exact four-decimal amount beside every rounded one. */
  precise: boolean;
  /** Names for ids, from cached catalog reads and what the user chose; `null` when the name is not known. */
  partName: (id: string) => string | null;
  categoryName: (id: string) => string | null;
  warehouseName: (id: string) => string | null;
  supplierName: (id: string) => string | null;
}

const NO_NAMES = (): null => null;
export const ReportDisplayContext = createContext<ReportDisplay>({
  language: 'en',
  precise: false,
  partName: NO_NAMES,
  categoryName: NO_NAMES,
  warehouseName: NO_NAMES,
  supplierName: NO_NAMES,
});
export const useReportDisplay = (): ReportDisplay => useContext(ReportDisplayContext);

/** A figure that is unknown: the reason in words, never a zero and never a dash alone. */
export function Unknown({ reason }: { reason: string }) {
  const { t } = useTranslation('inventoryReports');
  return (
    <Stack component="span" direction="row" spacing={0.5} alignItems="center" sx={{ display: 'inline-flex' }}>
      <Chip size="small" variant="outlined" color="warning" label={t('unknown')} />
      <Typography component="span" variant="caption" color="text.secondary">
        {t(reasonKey(reason))}
      </Typography>
    </Stack>
  );
}

/** A metric: its value through `render`, or the reason it is unknown. A measured zero is shown as the zero it is. */
export function MetricValue<T>({ metric, render }: { metric: WireMetric<T>; render: (value: T) => ReactNode }) {
  return metric.available ? <>{render(metric.value)}</> : <Unknown reason={metric.reason} />;
}

/** Whole-number or ratio figure the server already rounded. */
export function Figure({ value }: { value: number }) {
  const { language } = useReportDisplay();
  return <>{formatFigure(value, language)}</>;
}

/** Money: the once-rounded figure when the server sent one, otherwise the exact text; exact beside it on request. */
export function Money({ cents4, cents }: { cents4: string; cents?: number | null }) {
  const { language, precise } = useReportDisplay();
  const view = moneyView(cents4, cents, language);
  return (
    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
      {view.primary}
      {precise && view.exact !== null && (
        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {view.exact}
        </Typography>
      )}
    </Box>
  );
}

/** Whole cents the server sent as an integer (a signed price variance). */
export function Cents({ cents }: { cents: number }) {
  const { language } = useReportDisplay();
  return <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{formatRoundedEuro(cents, language)}</Box>;
}

/** Exact money text of a metric that carries only the exact text (turnover numerators). */
export function ExactMoney({ cents4 }: { cents4: string }) {
  const { language } = useReportDisplay();
  return <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{formatExactEuro(cents4, language)}</Box>;
}

/**
 * The server's whole-result total (every page, not this page) with its exact value, and the rounding difference
 * against the sum of the rounded rows ONLY when there is one.
 */
export function ReconciledTotal({ label, total }: { label: string; total: WireReconciliation }) {
  const { t } = useTranslation('inventoryReports');
  const { language } = useReportDisplay();
  const exact = formatExactEuro(total.totalCents4, language);
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" component="p">
        <Money cents4={total.totalCents4} cents={total.totalCents} />
      </Typography>
      <Typography variant="caption" color="text.secondary" component="p">
        {t('totals.wholeResult')} {t('totals.exact', { amount: exact })}
      </Typography>
      {total.roundingDifferenceCents !== 0 && (
        <Typography variant="caption" color="warning.main" component="p">
          {t('totals.roundingDifference', {
            sum: formatRounded(total.sumOfRoundedRowsCents, language),
            difference: formatRounded(total.roundingDifferenceCents, language),
          })}
        </Typography>
      )}
    </Box>
  );
}

function formatRounded(cents: number, language: string): string {
  return moneyView('0.0000', cents, language).primary;
}

/** The window the server used, in the tenant's calendar, with the exact bounds. */
export function WindowInfo({ window: w }: { window: WireWindow }) {
  const { t } = useTranslation('inventoryReports');
  const { language } = useReportDisplay();
  return (
    <Typography variant="body2" color="text.secondary">
      {t('window.summary', { from: w.fromDate, to: w.toDate, days: w.calendarDays, zone: w.timeZone })}{' '}
      {t('window.bounds', {
        start: formatInstant(w.startUtc, w.timeZone, language),
        end: formatInstant(w.endUtc, w.timeZone, language),
      })}
    </Typography>
  );
}

/** A caption line about the date or window a report describes. */
export function WindowInfoText({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  );
}

/** Where a financial figure comes from, so nothing is read as FIFO that is not. */
export function SourceNote({ source }: { source: WireValueSource }) {
  const { t } = useTranslation('inventoryReports');
  return (
    <Typography variant="caption" color="text.secondary" component="p">
      {t(source.value === 'fifo_layers' ? 'source.fifoLayers' : 'source.frozenInvoiceLines')}{' '}
      {source.legacyMovementCost === 'not_used' ? t('source.legacyNotUsed') : ''}{' '}
      {source.unknownCost === 'never_valued' ? t('source.unknownNeverValued') : ''}
    </Typography>
  );
}

/** A table that scrolls sideways on a narrow screen instead of squeezing its figures. */
export function ReportTable({ label, children }: { label: string; children: ReactNode }) {
  return (
    <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
      <Table size="small" aria-label={label}>
        {children}
      </Table>
    </TableContainer>
  );
}

/** One labelled figure of a summary strip. */
export function Stat({ label, children, caption }: { label: string; children: ReactNode; caption?: ReactNode }) {
  return (
    <Box sx={{ minWidth: 140 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" component="div">
        {children}
      </Typography>
      {caption !== undefined && (
        <Typography variant="caption" color="text.secondary" component="div">
          {caption}
        </Typography>
      )}
    </Box>
  );
}

export function PartialWarning({ children }: { children: ReactNode }) {
  const { t } = useTranslation('inventoryReports');
  return (
    <Alert severity="warning" icon={false} sx={{ my: 1 }}>
      <strong>{t('partial.label')}</strong> {children}
    </Alert>
  );
}

export type NamedKind = 'part' | 'category' | 'warehouse' | 'supplier' | 'booking';

/**
 * An entity by name. A part shows the name the server sent, else a cached one, else its SKU; a group id with no name
 * known to the portal shows a plain "not in the loaded catalog" label with a short reference beside it, so rows stay
 * apart without an id ever being the label. A `null` id (profitability's "no booking") is said in words.
 */
export function Named({ kind, id, sku, name }: { kind: NamedKind; id: string | null; sku?: string | null; name?: string | null }) {
  const { t } = useTranslation('inventoryReports');
  const display = useReportDisplay();
  if (id === null) return <>{t('names.noBooking')}</>;
  let label: string | null = null;
  if (kind === 'part') label = sku && name ? `${sku} · ${name}` : (display.partName(id) ?? (sku ? sku : null));
  else if (kind === 'category') label = display.categoryName(id);
  else if (kind === 'warehouse') label = display.warehouseName(id);
  else if (kind === 'supplier') label = display.supplierName(id);
  if (label !== null) return <>{label}</>;
  return (
    <>
      {t(`names.${kind}Unresolved`)}
      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, fontFamily: 'monospace' }}>
        {id.slice(0, 8)}
      </Typography>
    </>
  );
}

/** Rows visible on this page of the whole result. */
export function pageRange(p: WirePagination, rows: number): { from: number; to: number } {
  return { from: rows === 0 ? 0 : p.offset + 1, to: p.offset + rows };
}
